import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../../contexts/AuthContext';
import { auth, functions } from '../../config/firebase';
import AdminMfaChallenge from './AdminMfaChallenge';

const navSections = [
  { label: 'Main', items: [
    { path: '/admin', icon: '📊', label: 'Overview', exact: true },
    { path: '/admin/bookings', icon: '📋', label: 'Bookings' },
    { path: '/admin/drivers', icon: '🚗', label: 'Drivers' },
    { path: '/admin/riders', icon: '👥', label: 'Riders' },
    { path: '/admin/vehicles', icon: '🚘', label: 'Vehicles' },
  ]},
  { label: 'Configuration', items: [
    { path: '/admin/fares', icon: '💰', label: 'Fare Config' },
    { path: '/admin/zones', icon: '🗺️', label: 'Zones' },
    { path: '/admin/promos', icon: '🎟️', label: 'Promo Codes' },
  ]},
  { label: 'Support', items: [
    { path: '/admin/support', icon: '🎫', label: 'Support Centre' },
    { path: '/admin/lost-items', icon: '📦', label: 'Lost Items' },
    { path: '/admin/claims', icon: '🔧', label: 'Damage Claims' },
    { path: '/admin/flagged-messages', icon: '🚩', label: 'Flagged Messages' },
  ]},
  { label: 'Safety', items: [
    { path: '/admin/safety', icon: '🛡️', label: 'Safety Dashboard' },
    { path: '/admin/fatigue', icon: '😴', label: 'Fatigue Overview' },
    { path: '/admin/training', icon: '📚', label: 'Training' },
    { path: '/admin/ratings', icon: '⭐', label: 'Ratings' },
    { path: '/admin/audit-log', icon: '📜', label: 'Audit Log' },
  ]},
  { label: 'Compliance', items: [
    { path: '/admin/document-expiry', icon: '📄', label: 'Document Expiry' },
    { path: '/admin/recheck', icon: '🔄', label: 'Annual Rechecks' },
    { path: '/admin/incidents', icon: '⚠️', label: 'Incidents' },
    { path: '/admin/complaints', icon: '📣', label: 'Complaints' },
    { path: '/admin/levy', icon: '🏛️', label: 'Levy Reports' },
  ]},
  { label: 'Finance', items: [
    { path: '/admin/finance', icon: '💵', label: 'Financial Reports' },
    { path: '/admin/corporate', icon: '🏢', label: 'Corporate Accounts' },
    { path: '/admin/deviations', icon: '🧭', label: 'Route deviations' },
  ]},
];

const superAdminItems = [
  { path: '/admin/distribution', icon: '💸', label: 'Annual Distribution' },
  { path: '/admin/settings', icon: '⚙️', label: 'Settings' },
];
const accountItems = [
  { path: '/admin/mfa-setup', icon: '🔐', label: 'Two-Factor Auth' },
];

export default function AdminLayout() {
  const { userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const isSuperAdmin = userProfile?.role === 'superAdmin';

  // Set sidebar default + track viewport: open on desktop, closed on mobile.
  // This effect is independent of the MFA-check effect below.
  useEffect(() => {
    const apply = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setSidebarOpen(!mobile);
    };
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, []);

  // Batch 16 M3 — MFA gate
  // States: 'checking' | 'verified' | 'enrolled-needs-verify' | 'not-enrolled'
  const [mfaState, setMfaState] = useState('checking');

  useEffect(() => {
    const checkMfa = async () => {
      if (!auth.currentUser) { setMfaState('not-enrolled'); return; }
      try {
        const tokenResult = await auth.currentUser.getIdTokenResult();
        if (tokenResult.claims.mfaVerified && tokenResult.claims.mfaVerifiedAt) {
          // Verified claim valid for 12 hours
          if (Date.now() - tokenResult.claims.mfaVerifiedAt < 12 * 60 * 60 * 1000) {
            setMfaState('verified');
            return;
          }
        }
        const callable = httpsCallable(functions, 'getMfaStatus');
        const result = await callable();
        setMfaState(result.data?.enrolled ? 'enrolled-needs-verify' : 'not-enrolled');
      } catch (e) {
        console.warn('MFA status check failed:', e.message);
        setMfaState('not-enrolled');
      }
    };
    checkMfa();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Close the drawer after navigating, on mobile only.
  const handleNavClick = () => {
    if (isMobile) setSidebarOpen(false);
  };

  const linkStyle = (isActive) => ({
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderRadius: 10,
    color: isActive ? '#2dd4a8' : '#94a3b8', background: isActive ? 'rgba(45,212,168,0.08)' : 'transparent',
    textDecoration: 'none', fontSize: '0.9rem', fontWeight: isActive ? 600 : 400, transition: 'all 0.2s',
  });

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Mobile backdrop — only when drawer is open on mobile */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 z-[90]"
          style={{ background: 'rgba(10,22,40,0.5)' }}
        />
      )}

      {/* Sidebar */}
      <aside
        className={isMobile ? 'fixed top-0 left-0 z-[100] shrink-0' : 'sticky top-0 shrink-0'}
        style={{
          width: sidebarOpen ? 260 : 0,
          height: '100vh',
          background: '#0a1628',
          padding: sidebarOpen ? '20px 12px' : 0,
          overflow: 'hidden',
          overflowY: sidebarOpen ? 'auto' : 'hidden',
          transition: 'all 0.3s',
          boxShadow: isMobile && sidebarOpen ? '0 0 40px rgba(0,0,0,0.4)' : 'none',
        }}>
        {/* Brand */}
        <div className="flex items-center gap-2.5 px-2 pt-2 pb-6 mb-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="w-9 h-9 rounded-[10px] flex items-center justify-center font-extrabold text-[15px] text-brand-dark" style={{ background: 'linear-gradient(135deg, #2dd4a8, #6ee7b7)' }}>R</div>
          <span className="font-serif text-[1.2rem] text-white">Reservo Admin</span>
        </div>

        {navSections.map((section) => (
          <div key={section.label} className="mb-4">
            <div className="text-[0.7rem] font-bold uppercase px-2 mb-1.5" style={{ color: '#475569', letterSpacing: 1 }}>
              {section.label}
            </div>
            {section.items.map((item) => (
              <NavLink key={item.path} to={item.path} end={item.exact} onClick={handleNavClick}
                style={({ isActive }) => linkStyle(isActive)}>
                <span className="text-base">{item.icon}</span>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        ))}

        {isSuperAdmin && (
          <div className="mb-4">
            <div className="text-[0.7rem] font-bold uppercase px-2 mb-1.5" style={{ color: '#475569', letterSpacing: 1 }}>System</div>
            {superAdminItems.map((item) => (
              <NavLink key={item.path} to={item.path} onClick={handleNavClick}
                style={({ isActive }) => linkStyle(isActive)}>
                <span className="text-base">{item.icon}</span>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        )}
        {/* Batch 16 M3 — Account section (visible to all admins) */}
        <div className="mb-4">
          <div className="text-[0.7rem] font-bold uppercase px-2 mb-1.5" style={{ color: '#475569', letterSpacing: 1 }}>My Account</div>
          {accountItems.map((item) => (
            <NavLink key={item.path} to={item.path} onClick={handleNavClick}
              style={({ isActive }) => linkStyle(isActive)}>
              <span className="text-base">{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>
        {/* User + Logout */}
        <div className="mt-auto px-2 py-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="text-[0.85rem] mb-1" style={{ color: '#94a3b8' }}>{userProfile?.firstName} {userProfile?.lastName}</div>
          <div className="text-[0.75rem] mb-3" style={{ color: '#475569' }}>{userProfile?.role}</div>
          <button onClick={handleLogout}
            className="w-full px-4 py-2 rounded-lg bg-transparent cursor-pointer text-[0.85rem]"
            style={{ border: '1px solid rgba(255,255,255,0.1)', color: '#94a3b8' }}>Sign Out</button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        {/* Top Bar */}
        <div className="px-6 py-3 bg-white flex items-center justify-between sticky top-0 z-10" style={{ borderBottom: '1px solid #e2e8f0' }}>
          <button onClick={() => setSidebarOpen(!sidebarOpen)}
            className="px-3 py-2 rounded-lg bg-white cursor-pointer" style={{ border: '1px solid #e2e8f0' }}>☰</button>
          <div className="text-[0.85rem] text-muted">{new Date().toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
        </div>

        {/* Page Content */}
        <div className="p-6">
          {mfaState === 'checking' && (
            <div className="p-10 text-center text-muted">Verifying admin access…</div>
          )}
          {mfaState === 'enrolled-needs-verify' && (
            <AdminMfaChallenge onSuccess={() => setMfaState('verified')} />
          )}
          {mfaState === 'not-enrolled' && (
            <>
              {/* Soft-gate before MFA_DEPRECATION_DATE — show banner and allow access.
                  After the cutover date, replace this with a hard redirect to /admin/mfa-setup. */}
              <div className="p-3.5 rounded-[10px] mb-4 text-[0.85rem]" style={{ background: '#fef3c7', border: '1px solid #fbbf24' }}>
                ⚠ Your admin account is not yet protected by two-factor authentication.
                {' '}
                <NavLink to="/admin/mfa-setup" style={{ color: '#92400e', fontWeight: 700 }}>Set it up now</NavLink>
                {' '}— it will become mandatory soon.
              </div>
              <Outlet />
            </>
          )}
          {mfaState === 'verified' && <Outlet />}
        </div>
      </main>
    </div>
  );
}